import React, { useEffect, useRef, useState } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import InfiniteScroll from "react-infinite-scroll-component";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import { Typography, Card, CircularProgress, styled, Avatar, Tooltip, InputAdornment, Chip, TextField, IconButton } from "@mui/material";
import Filter from "../common/Filter";
import { useDispatch, useSelector } from "react-redux";
import { GET_ALL_JOBS } from "../../redux/api/config";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { useNavigate } from "react-router-dom";
import { logoutClear, setErrorMessage } from "../../redux/actions/appActions";
import { APP_CUSTOM_ERROR, JOB_ACTIONS } from "../../constants/AppVarConstants";
import { ROUTE_COMPLETE_PROFILE, ROUTE_JOB_CONGRATS, ROUTE_JOB_DETAILS, ROUTE_MAIN } from "../../constants/routes";
import { jobAction, setJobSearchFilters, clearJobSearchFilters } from "../../redux/actions/jobsActions";
import { AccountCircle, Close, Search } from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import SearchFilter from "../common/SearchFilter";
import { useLocation } from "react-router-dom";
import CandidateDeleteAccount from "../profile/CandidateDeleteAccount";
import { isCandidateDeleteAccountAllowed } from "../../constants/candidateDeleteTestAccounts";

const EllipsisTooltip = ({ text }: any) => {
  const [open, setOpen] = useState(false);
  const content = Array.isArray(text) ? text.join(", ") : text || "";

  const handleTooltipToggle = () => {
    setOpen(!open);
  };

  return (
    <Box
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ml: 1,
        maxWidth: "80%",
      }}
    >
      <Tooltip title={content} arrow open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)}>
        <Typography
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "14px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
          onClick={handleTooltipToggle}
        >
          {content}
        </Typography>
      </Tooltip>
    </Box>
  );
};

const Logo = styled("img")({
  aspectRatio: "1.89",
  width: "75px",
});

const Header = styled("header")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
  padding: "20px",
}));

const initialTabIndex = 0;

const BrowseJobsNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.app.user);
  const { profileStatus, profileImage, phoneNumber } = user?.user || {};
  const { jobSearchFilters } = useSelector((state: any) => state.jobs);
  const [hasMore, setHasMore] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [openSearchFilter, setOpenSearchFilter] = useState(false);
  const [selectedTab, setSelectedTab] = useState(initialTabIndex);
  const [applyingJobId, setApplyingJobId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const defaultFilters = {
    type: "",
    function: [],
    company: [],
    jobRole: [],
    jobType: [],
    workMode: [],
    location: [],
    city: [],
    industry: [],
    minSal: null,
    maxSal: null,
    minExp: "",
    maxExp: "",
  };

  // Initialize currentFilters based on Redux state and location
  const [currentFilters, setCurrentFilters] = useState<any>(() => {
    // If not coming from JobDetails, use default filters
    if (location.state?.fromJobDetails !== true) {
      return defaultFilters;
    }
    // Otherwise, use persisted filters from Redux or defaults
    return jobSearchFilters && Object.keys(jobSearchFilters).length > 0 ? jobSearchFilters : defaultFilters;
  });

  const Logout = () => {
    dispatch(
      logoutClear(() => {
        navigate(ROUTE_MAIN, { replace: true });
      })
    );
  };

  // Clear filters and sync Redux state on route change
  useEffect(() => {
    if (location.state?.fromJobDetails !== true) {
      dispatch(clearJobSearchFilters(defaultFilters));
    }
  }, [location.pathname]);

  const fetchJobs = (
    flag: boolean,
    resetOffset: boolean,
    jobAction = false,
    filter = {
      type: "",
      company: [],
      function: [],
      jobRole: [],
      industry: [],
      jobType: [],
      workMode: [],
      city: [],
      location: [],
      minSal: "",
      maxSal: "",
      minExp: "",
      maxExp: "",
      ...currentFilters,
    }
  ) => {
    if (!flag) {
      setLoading(true);
    }
    const filters: any = {};
    const rangeFilters: any = {};
    if (filter?.type) filters.category = [filter?.type];
    if (filter?.company?.length) filters.company = filter.company;
    if (filter?.industry?.length) filters.industry = filter.industry;
    if (filter?.function?.length) filters.function = filter.function;
    if (filter?.jobRole?.length) filters.job_role = filter.jobRole;
    if (filter?.jobType?.length) filters.job_type = filter.jobType;
    if (filter?.workMode?.length) filters.work_mode = filter.workMode;
    // if (filter?.city?.length) {
    //   filters.city = filter.city;
    // }

    if (filter?.location?.length) {
      filters.location = filter.location;
    }

    if (filter?.minSal || filter?.maxSal) {
      rangeFilters.salary = [filter.minSal || null, filter.maxSal || null];
    }
    if (filter?.minExp || filter?.maxExp) {
      rangeFilters.experience = [filter.minExp || null, filter.maxExp || null];
    }
    const payload = {
      searchQuery: searchQuery || undefined, // Add search query to payload
      page: jobAction ? currentPage : resetOffset ? 1 : currentPage + 1,
      fetchSize: 10,
      filters,
      rangeFilters,

      // sort: ["sort_param", "order"],
    };
    API_MIDDLEWARE(GET_ALL_JOBS, "POST", { "Content-Type": "application/json" }, payload)
      .then((res: any) => {
        const { records, hasNext, page, totalRecords } = res?.result || {};
        const jobs: any = resetOffset ? [...records] : [...data, ...records];
        setData(jobs);
        setCurrentPage(page);
        setHasMore(hasNext);
        setLoading(false);
        setTotal(totalRecords);
        if (scrollRef.current) {
          const currentScrollTop =
            window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
          scrollRef.current.scrollTop = currentScrollTop || 0;
        }
        setOpenFilter(false);
        setOpenSearchFilter(false);
      })
      .catch((err: any) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchJobs(false, true);
    dispatch(setJobSearchFilters(currentFilters));
  }, [currentFilters]);

  useEffect(() => {
    if (searchQuery !== undefined) {
      fetchJobs(false, true);
    }
  }, [searchQuery]);

  const handleTabChange = (event: any, newValue: any) => {
    setSelectedTab(newValue);
  };

  const handleSaveFilter = (filter: any) => {
    setCurrentFilters(filter);
  };

  const handleJobAction = (jobId: string, action: string) => {
    if (applyingJobId) return;
    setApplyingJobId(jobId);
    dispatch(
      jobAction(jobId, action, () => (action === JOB_ACTIONS.APPLIED ? navigate(ROUTE_JOB_CONGRATS) : {}), () =>
        setApplyingJobId(null)
      )
    );
  };

  const handleFilterRemove = (index: number, type: string) => {
    const updatedFilters = { ...currentFilters };

    if (index === -1) {
      updatedFilters["minSal"] = null;
      updatedFilters["maxSal"] = null;
    } else {
      updatedFilters[type] = updatedFilters[type].filter((_: any, i: number) => i !== index);
    }

    setCurrentFilters(updatedFilters);
  };

  // Check if there are any active filters
  const hasActiveFilters = () => {
    return (
      (currentFilters?.company?.length > 0) ||
      (currentFilters?.function?.length > 0) ||
      (currentFilters?.jobRole?.length > 0) ||
      (currentFilters?.location?.length > 0) ||
      (currentFilters?.jobType?.length > 0) ||
      (currentFilters?.workMode?.length > 0) ||
      (currentFilters?.minSal && currentFilters?.maxSal)
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "rgb(255, 137, 96)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Header onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}>
          <Logo src="/images/client-website/seevi-logo.png" alt="Logo" />
        </Header>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box
            sx={{
              px: 1,
              backgroundColor: "#fff",
              borderRadius: 16,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
            onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}
          >
            <Box sx={{ display: "flex", alignItems: "center", fontFamily: "Poppins" }}>
              <Box component="span">My Dashboard</Box>
              <Avatar alt="User Avatar" src={profileImage} sx={{ ml: 1 }}>
                <AccountCircle />
              </Avatar>
            </Box>
          </Box>
          <Box
            sx={{ mt: 0, mr: 2, display: "flex", alignItems: "center" }}
            onClick={() => Logout()}
            style={{ cursor: "pointer" }}
          >
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
              <LogoutIcon />
            </InputAdornment>
          </Box>
        </Box>
      </Box>
      {isCandidateDeleteAccountAllowed(phoneNumber) ? (
        <Box sx={{ backgroundColor: "#F4F4F4", px: 2, pt: 0.5, pb: 0.5 }}>
          <CandidateDeleteAccount phoneNumber={phoneNumber} />
        </Box>
      ) : null}
      <Box sx={{ backgroundColor: "#F4F4F4", pt: 1.5, px: 2 }}>
        <Stack direction="column" alignItems="center">
          {/* Search box and filter icon row - positioned at the top left */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              marginBottom: "4px", // Reduced from "8px"
              position: "relative",
            }}
          >
            {/* Search box and filter row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* Search Box - starts from left */}
              <TextField
                placeholder="company, location, skills"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  flex: 1,
                  maxWidth: "300px",
                  "& .MuiOutlinedInput-root": {
                    height: "50px",
                    "& fieldset": {
                      borderColor: "#756DE9",
                    },
                    "&:hover fieldset": {
                      borderColor: "#756DE9",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#756DE9",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                    padding: "12px 14px",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#756DE9" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        sx={{ padding: "4px" }}
                      >
                        <Close fontSize="small" sx={{ color: "#756DE9" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Filter Component - positioned to the right of search box */}
              <Box>
                <Filter
                  filters={currentFilters}
                  handleSaveFilter={handleSaveFilter}
                  open={openFilter}
                  setOpen={setOpenFilter}
                />
              </Box>
            </Box>

            {/* Matches found text - right below search and filter */}
            <Box sx={{ marginTop: "4px", marginBottom: "0px" }}>
              <p style={{ color: "#756DE9", fontSize: "12px", fontWeight: "500", margin: 0 }}>
                {total} matches found
              </p>
            </Box>
          </Box>

          {/* Tabs section - only show if there are active filters */}
          {hasActiveFilters() && (
            <Box sx={{ overflowX: "auto", width: "100%", marginTop: "8px" }}>
              <Tabs
                variant="scrollable"
                scrollButtons="auto"
                indicatorColor="primary"
                value={selectedTab}
                onChange={handleTabChange}
                sx={{
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                  "& .MuiTab-root": {
                    fontSize: "12px",
                    marginRight: "5px",
                    "&.Mui-selected": {
                      backgroundColor: "#e5e3fd",
                      color: "#756DE9",
                      borderRadius: "100px",
                      padding: "0px 15px",
                      fontWeight: 550,
                      textTransform: "none",
                    },
                    backgroundColor: "#e5e3fd",
                    color: "#756DE9",
                    borderRadius: "100px",
                    padding: "0px 20px",
                    fontWeight: 550,
                    textTransform: "none",
                  },
                  minWidth: "100%",
                  boxSizing: "border-box",
                  width: "100%",
                  paddingRight: "60px",
                  overflowX: "auto",
                }}
              >
                {[...currentFilters?.company].map((value: any, index: any) => (
                  <Tab
                    key={index}
                    label={
                      <Box display="flex" alignItems="center">
                        {value}
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterRemove(index, "company"); // Function to handle removing the filter
                          }}
                          style={{ cursor: "pointer", marginLeft: "5px", marginTop: "3px" }} // Add styles for visibility
                        >
                          <Close fontSize="small" sx={{ color: "#756DE9", fontSize: "16px" }} />
                        </span>
                      </Box>
                    }
                  />
                ))}
                {[...currentFilters?.function].map((value: any, index: any) => (
                  <Tab
                    key={index}
                    label={
                      <Box display="flex" alignItems="center">
                        {value}
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterRemove(index, "function"); // Function to handle removing the filter
                          }}
                          style={{ cursor: "pointer", marginLeft: "5px", marginTop: "3px" }} // Add styles for visibility
                        >
                          <Close fontSize="small" sx={{ color: "#756DE9", fontSize: "16px" }} />
                        </span>
                      </Box>
                    }
                  />
                ))}
                {[...currentFilters?.jobRole].map((value: any, index: any) => (
                  <Tab
                    key={index}
                    label={
                      <Box display="flex" alignItems="center">
                        {value}
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterRemove(index, "jobRole"); // Function to handle removing the filter
                          }}
                          style={{ cursor: "pointer", marginLeft: "5px", marginTop: "3px" }} // Add styles for visibility
                        >
                          <Close fontSize="small" sx={{ color: "#756DE9", fontSize: "16px" }} />
                        </span>
                      </Box>
                    }
                  />
                ))}
                {[...currentFilters?.location].map((value: any, index: any) => (
                  <Tab
                    key={index}
                    label={
                      <Box display="flex" alignItems="center">
                        {value}
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterRemove(index, "location"); // Function to handle removing the filter
                          }}
                          style={{ cursor: "pointer", marginLeft: "5px", marginTop: "3px" }} // Add styles for visibility
                        >
                          <Close fontSize="small" sx={{ color: "#756DE9", fontSize: "16px" }} />
                        </span>
                      </Box>
                    }
                  />
                ))}
                {[...currentFilters?.jobType].map((value: any, index: any) => (
                  <Tab
                    key={index}
                    label={
                      <Box display="flex" alignItems="center">
                        {value}
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterRemove(index, "jobType"); // Function to handle removing the filter
                          }}
                          style={{ cursor: "pointer", marginLeft: "5px", marginTop: "3px" }} // Add styles for visibility
                        >
                          <Close fontSize="small" sx={{ color: "#756DE9", fontSize: "16px" }} />
                        </span>
                      </Box>
                    }
                  />
                ))}
                {[...currentFilters?.workMode].map((value: any, index: any) => (
                  <Tab
                    key={index}
                    label={
                      <Box display="flex" alignItems="center">
                        {value}
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterRemove(index, "workMode"); // Function to handle removing the filter
                          }}
                          style={{ cursor: "pointer", marginLeft: "5px", marginTop: "3px" }} // Add styles for visibility
                        >
                          <Close fontSize="small" sx={{ color: "#756DE9", fontSize: "16px" }} />
                        </span>
                      </Box>
                    }
                  />
                ))}
                {currentFilters?.minSal && currentFilters?.maxSal ? (
                  <Tab
                    label={
                      <Box display="flex" alignItems="center">
                        {`${currentFilters?.minSal} - ${currentFilters?.maxSal}`}
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterRemove(-1, "minSal"); // Function to handle removing the filter
                          }}
                          style={{ cursor: "pointer", marginLeft: "5px", marginTop: "3px" }} // Add styles for visibility
                        >
                          <Close fontSize="small" sx={{ color: "#756DE9", fontSize: "16px" }} />
                        </span>
                      </Box>
                    }
                  />
                ) : null}
              </Tabs>
            </Box>
          )}

          {/* Keep SearchFilter as is */}
          <Box
            sx={{
              position: "absolute",
              right: "0px",
              top: "80px",
              backgroundColor: "rgba(244, 244, 244)",
            }}
          >
            <SearchFilter
              filters={currentFilters}
              handleSaveFilter={handleSaveFilter}
              open={openSearchFilter}
              setOpen={setOpenSearchFilter}
            />
          </Box>
        </Stack>

        {/* Remove the old Stack with matches found (lines 565-571) */}

        <Box sx={{ flexGrow: 1, paddingBottom: "60px", marginTop: "4px" }}> {/* Changed from className="mt-1" to marginTop: "4px" */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <InfiniteScroll
              dataLength={data.length}
              next={() => fetchJobs(true, false)}
              hasMore={hasMore}
              loader={<h4>Loading...</h4>}
              endMessage={
                data.length === 0 ? (
                  <Box component="p" sx={{ textAlign: "center", mt: 2 }}>
                    <b>
                      We couldn&apos;t find any matches. Modify your filters and try again.
                    </b>
                  </Box>
                ) : (
                  <Box>
                  </Box>
                )
              }
            >
              {data?.map((el: any, idx: number) => {
                return (
                  <Grid container key={idx} spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box
                        style={{
                          padding: "20px",
                          background: `#ffffff`,
                          borderRadius: "20px",
                          border: "1px solid #756DE9",
                        }}
                        className="mt-3"
                      >
                        <Stack direction="row" justifyContent="space-between">
                          <Box>
                            <Stack direction="row">
                              <img
                                src={el?.companyDetails?.logo}
                                alt=""
                                className="img-fluid"
                                style={{ width: "70px" }}
                              />
                              <Box className="ms-3">
                                <h4 className="text-dark">{el?.title}</h4>
                                <p style={{ color: "#ADADAD" }}>{el?.companyDetails?.name}</p>
                              </Box>
                            </Stack>
                          </Box>
                          {el?.jobCategory && (
                            <Chip
                              label={el.jobCategory}
                              sx={{
                                backgroundColor:
                                  el.jobCategory?.toLowerCase() === "internship" ? "#E6F0FE" : "#FFF4E6",
                                color:
                                  el.jobCategory?.toLowerCase() === "internship" ? "#1976D2" : "#FF9800",
                                fontWeight: 500,
                                fontSize: "14px",
                                height: "28px",
                                borderRadius: "16px",
                                px: 1.5,
                              }}
                            />
                          )}
                        </Stack>

                        <Divider sx={{ backgroundColor: "#0000000f" }} className="mt-2 mb-3" />

                        <Stack direction="row" justifyContent="space-between">
                          <Stack
                            direction="row"
                            alignItems="center"
                            padding="8px"
                            width="28%"
                            borderRadius="5px"
                            sx={{ backgroundColor: "rgb(230, 228, 254, 0.41)" }}
                          >
                            <img src="/images/saved-jobs/icon-1.png" alt="" width="16px" />

                            <EllipsisTooltip text={el?.location} />
                          </Stack>

                          <Stack
                            direction="row"
                            // justifyContent="space-evenly"
                            alignItems="center"
                            padding="8px"
                            width="28%"
                            borderRadius="5px"
                            sx={{ backgroundColor: "rgb(230, 228, 254, 0.41)" }}
                          >
                            <img src="/images/complete-profile/CTC.svg" alt="" width="16px" />

                            <EllipsisTooltip text={`${el?.minCtc} - ${el?.maxCtc}`} />
                          </Stack>

                          <Stack
                            direction="row"
                            // justifyContent="space-evenly"
                            alignItems="center"
                            padding="8px"
                            width="28%"
                            borderRadius="5px"
                            sx={{ backgroundColor: "rgb(230, 228, 254, 0.41)" }}
                          >
                            <img src="/images/complete-profile/Experience.svg" alt="" width="16px" />

                            <EllipsisTooltip text={`${el?.minExperience} - ${el?.maxExperience}`} />
                          </Stack>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" className="mt-4">
                          <Stack
                            direction="row"
                            // justifyContent="space-evenly"
                            alignItems="left"
                            padding="8px"
                            width="28%"
                            borderRadius="5px"
                            sx={{ backgroundColor: "rgb(230, 228, 254, 0.41)" }}
                          >
                            <img src="/images/degree.png" alt="" width="16px" />
                            <Box
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                ml: 1,
                                maxWidth: "80%",
                              }}
                            >
                              <EllipsisTooltip text={el?.preferredQualification} />
                            </Box>
                          </Stack>

                          <Stack
                            direction="row"
                            // justifyContent="space-evenly"
                            alignItems="center"
                            padding="8px"
                            width="28%"
                            borderRadius="5px"
                            sx={{ backgroundColor: "rgb(230, 228, 254, 0.41)" }}
                          >
                            <img src="/images/complete-profile/Job Type.svg" alt="" width="16px" />

                            <EllipsisTooltip text={el?.jobType} />
                          </Stack>

                          <Stack
                            direction="row"
                            // justifyContent="space-evenly"
                            alignItems="center"
                            padding="8px"
                            width="28%"
                            borderRadius="5px"
                            sx={{ backgroundColor: "rgb(230, 228, 254, 0.41)" }}
                          >
                            <img src="/images/complete-profile/Job Mode.svg" alt="" width="16px" />
                            <EllipsisTooltip text={el?.workMode} />
                          </Stack>
                        </Stack>

                        <Divider sx={{ backgroundColor: "#0000000f" }} className="mt-3 mb-3" />

                        {el?.functions?.length ? (
                          <>
                            <Stack direction="row" alignItems="end">
                              <img src="/images/complete-profile/Functions.svg" alt="" className="me-2" />
                              <p style={{ color: "#756DE9", fontSize: "12px", fontWeight: "500" }}>Functions</p>
                            </Stack>
                            <Box className="mt-3">
                              <p
                                dangerouslySetInnerHTML={{
                                  __html: el.functions.join(" <span style='color: orange'>|</span> "),
                                }}
                              />
                            </Box>
                          </>
                        ) : null}

                        {el?.skills?.length ? (
                          <>
                            <Stack direction="row" alignItems="end" className="mt-3">
                              <img src="/images/saved-jobs/icon-5.png" alt="" className="me-2" />
                              <p style={{ color: "#756DE9", fontSize: "12px", fontWeight: "500" }}>Key Skills</p>
                            </Stack>
                            <Box className="mt-3">
                              <p
                                dangerouslySetInnerHTML={{
                                  __html: el.skills.join(" <span style='color: orange'>|</span> "),
                                }}
                              />
                            </Box>
                          </>
                        ) : null}

                        {el?.tools?.length ? (
                          <>
                            <Stack direction="row" alignItems="end" className="mt-3">
                              <img src="/images/saved-jobs/icon-6.png" alt="" className="me-2" />
                              <p style={{ color: "#756DE9", fontSize: "12px", fontWeight: "500" }}>Tools Known</p>
                            </Stack>
                            <Box className="mt-3">
                              <p>
                                {el?.tools.map((tool: string, index: number) => (
                                  <React.Fragment key={index}>
                                    <span>{tool}</span>
                                    {index !== el.tools.length - 1 && <span style={{ color: "orange" }}> | </span>}
                                  </React.Fragment>
                                ))}
                              </p>
                            </Box>
                          </>
                        ) : null}
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Button
                            sx={{
                              backgroundColor: "#F5F6FA",
                              borderRadius: "99px",
                              fontSize: "16px",
                              textTransform: "capitalize",
                              width: "100%",
                              paddingY: "10px",
                              paddingX: 0,
                              color: "#756DE9",
                              "&:hover": {
                                backgroundColor: "#756DE9",
                              },
                            }}
                            variant="contained"
                            size="small"
                            className="w-100 mt-3 btn-pad"
                            onClick={() =>
                              navigate(`${ROUTE_JOB_DETAILS}/${el?.jobId}`, { state: { fromBrowseJobs: true } })
                            }
                          >
                            Know More
                          </Button>
                          {el?.applied ? null : (
                            <Button
                              sx={{
                                backgroundColor: "#6352CC",
                                color: "#fff",
                                borderRadius: "99px",
                                fontSize: "16px",
                                textTransform: "capitalize",
                                width: "100%",
                                paddingY: "10px",
                                paddingX: 0,
                                "&:hover": {
                                  backgroundColor: "#756DE9",
                                },
                              }}
                              variant="contained"
                              size="small"
                              className="w-100 mt-3 btn-pad"
                              disabled={!!applyingJobId}
                              onClick={() => handleJobAction(el?.jobId, JOB_ACTIONS.APPLIED)}
                            >
                              {applyingJobId === el?.jobId ? (
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                                  Applying…
                                </Box>
                              ) : (
                                "Apply"
                              )}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}
            </InfiniteScroll>
          )}
        </Box>
      </Box>
    </>
  );
};

export default BrowseJobsNew;
